import fileDownload from 'js-file-download';
import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import callApi from 'utils/api';
import { getUrlParameters } from 'utils/helpers';
import {
  addAbsenceFailure,
  addAbsenceSuccess,
  deleteAbsenceFailure,
  deleteAbsenceSuccess,
  downloadCSVFailure,
  downloadCSVSuccess,
  fetchAbsenceFailure,
  fetchAbsencesFailure,
  fetchAbsencesSuccess,
  fetchAbsenceSuccess,
  fetchNextAbsencesFailure,
  fetchNextAbsencesSuccess,
  fetchPlantsFailure,
  fetchPlantsSuccess,
  updateAbsenceFailure,
  updateAbsenceSuccess,
  uploadCSVFailure,
  uploadCSVSuccess,
} from './absence-actions';
import * as actionTypes from './constants';

export function* doFetchPlants(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'refdata/lookup/workcenters',
      options: {
        errorMessage: 'Could not fetch plants',
        retryAction: action,
      },
    });
    yield put(fetchPlantsSuccess(results));
  } catch (err) {
    yield put(fetchPlantsFailure(err));
  }
}

export function* doFetchAbsences(action) {
  const { page, technicianId, pageSize } = action.payload;
  try {
    const results = yield call(callApi, {
      // eslint-disable-next-line
      endpoint: `planning/unavailability?page=${page}&pageSize=${pageSize}&technicianId=${technicianId}`,
      options: {
        errorMessage: 'Could not fetch absences',
        retryAction: action,
      },
    });
    yield put(fetchAbsencesSuccess(results));
  } catch (err) {
    yield put(fetchAbsencesFailure(err));
  }
}

export const getNext = state => state.absence.next;
export function* doFetchNextAbsences(action) {
  try {
    const next = yield select(getNext);
    const results = yield call(callApi, {
      endpoint: `${next.uri.replace('api/', '/')}?${getUrlParameters({ params: next.query })}`,
      options: {
        errorMessage: 'Could not fetch next absences',
        retryAction: action,
      },
    });
    yield put(fetchNextAbsencesSuccess(results));
  } catch (err) {
    yield put(fetchNextAbsencesFailure(err));
  }
}

export function* doFetchAbsence(action) {
  const { id } = action.payload;
  try {
    const results = yield call(callApi, {
      endpoint: `planning/unavailability/${id}`,
      options: {
        errorMessage: `Could not fetch absence (${id})`,
        retryAction: action,
      },
    });
    yield put(fetchAbsenceSuccess(results));
  } catch (err) {
    yield put(fetchAbsenceFailure(err));
  }
}

export function* doAddAbsence(action) {
  const { description, duration, id, start } = action.payload;

  const body = {
    description,
    duration,
    start,
    technicianId: id,
  };

  try {
    const results = yield call(callApi, {
      endpoint: 'planning/unavailability',
      options: {
        method: 'PUT',
        body: JSON.stringify(body),
        errorMessage: 'Could not add absence',
        retryAction: action,
      },
    });
    yield put(addAbsenceSuccess(results));
  } catch (err) {
    yield put(addAbsenceFailure(err));
  }
}

export function* doUpdateAbsence(action) {
  const { description, duration, start, id } = action.payload;

  const body = {
    description,
    duration,
    start,
  };

  try {
    const results = yield call(callApi, {
      endpoint: `planning/unavailability/${id}`,
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        errorMessage: 'Could not update absence',
        retryAction: action,
      },
    });
    yield put(updateAbsenceSuccess(results));
  } catch (err) {
    yield put(updateAbsenceFailure(err));
  }
}

export function* doDownloadCSV(action) {
  // TODO: Add start time and date in future
  const { technicianId, onlyHeaders = false } = action.payload;

  try {
    const results = yield call(callApi, {
      endpoint: `planning/holiday/download?technicianId=${technicianId}&onlyHeaders=${onlyHeaders}`,
      options: {
        method: 'POST',
        errorMessage: 'Could not download csv',
        retryAction: action,
        acceptType: 'text/csv',
        contentType: 'text/csv',
      },
    });
    const headers = results.headers?.split(';');
    const filenameHeader = headers?.find(h => h.includes('filename'));
    const [, filename] = filenameHeader?.split('=');
    fileDownload(results.text, filename);
    yield put(downloadCSVSuccess(results));
  } catch (err) {
    yield put(downloadCSVFailure(err));
  }
}

export function* doUploadCSV(action) {
  try {
    // eslint-disable-next-line prefer-destructuring
    const file = action.payload.file;
    const data = new FormData();
    data.append('file', file, file.name);

    const results = yield call(callApi, {
      endpoint: `planning/holiday/upload?technicianId=${action.payload.technicianId}`,
      options: {
        method: 'POST',
        body: data,
        errorMessage: 'Could not upload CSV.',
        retryAction: action,
        disableContentType: true,
      },
    });
    yield put(uploadCSVSuccess(results));
  } catch (err) {
    yield put(uploadCSVFailure(err));
  }
}

export function* doDeleteAbsence(action) {
  const { id } = action.payload;
  try {
    yield call(callApi, {
      endpoint: `planning/unavailability/${id}`,
      options: {
        method: 'DELETE',
        errorMessage: `Could not delete absence (${id})`,
        retryAction: action,
      },
    });
    yield put(deleteAbsenceSuccess({ id }));
  } catch (err) {
    yield put(deleteAbsenceFailure(err));
  }
}

function* watchFetchPlants() {
  yield takeLatest(actionTypes.FETCH_PLANTS_REQUEST, doFetchPlants);
}

function* watchFetchAbsences() {
  yield takeLatest(actionTypes.FETCH_ABSENCES_REQUEST, doFetchAbsences);
}

function* watchFetchNextAbsences() {
  yield takeLatest(actionTypes.FETCH_ABSENCES_NEXT_REQUEST, doFetchNextAbsences);
}

function* watchFetchAbsence() {
  yield takeLatest(actionTypes.FETCH_ABSENCE_REQUEST, doFetchAbsence);
}

function* watchAddAbsence() {
  yield takeLatest(actionTypes.ADD_ABSENCE_REQUEST, doAddAbsence);
}

function* watchUpdateAbsence() {
  yield takeLatest(actionTypes.UPDATE_ABSENCE_REQUEST, doUpdateAbsence);
}

function* watchDownloadCSV() {
  yield takeLatest(actionTypes.DOWNLOAD_CSV, doDownloadCSV);
}

function* watchUploadCSV() {
  yield takeLatest(actionTypes.UPLOAD_CSV, doUploadCSV);
}

function* watchDeleteAbsence() {
  yield takeLatest(actionTypes.DELETE_ABSENCE_REQUEST, doDeleteAbsence);
}

export default [
  fork(watchFetchPlants),
  fork(watchFetchAbsences),
  fork(watchFetchNextAbsences),
  fork(watchFetchAbsence),
  fork(watchAddAbsence),
  fork(watchUpdateAbsence),
  fork(watchDownloadCSV),
  fork(watchUploadCSV),
  fork(watchDeleteAbsence),
];
